import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Box, Typography, Grid, Card, CardContent, CardMedia,
  TextField, InputAdornment, Chip, Button, Skeleton, Pagination,
  useMediaQuery, useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Explore as ExploreIcon,
  Star as StarIcon,
  AttachMoney as BudgetIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UnsplashAttribution from '../components/UnsplashAttribution';
import SEOHead from '../components/SEOHead';
import DocumentMarketingHero from '../components/DocumentMarketingHero';
import api from '../config/api';
import { DOCUMENT, documentPageCanvasSx, documentFilterChipSx, documentEyebrowSx } from '../constants/documentTheme';
import { partnerOutlineBtnSx } from '../components/PartnerListChrome';

const continentEmoji = {
  'Europe': '🇪🇺',
  'Asia': '🌏',
  'North America': '🌎',
  'South America': '🌎',
  'Africa': '🌍',
  'Oceania': '🌊',
};

const Destinations = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();

  const [destinations, setDestinations] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [continents, setContinents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [continent, setContinent] = useState(searchParams.get('continent') || '');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 24;

  // Fetch continents and featured destinations on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [continentRes, featuredRes] = await Promise.all([
          api.get('/destinations/continents'),
          api.get('/destinations/featured?limit=6'),
        ]);
        setContinents(continentRes.data);
        setFeatured(featuredRes.data);
      } catch (err) {
        console.error('Error fetching initial data:', err);
      }
    };
    fetchInitialData();
  }, []);

  // Fetch destinations with filters
  const fetchDestinations = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (continent) params.set('continent', continent);
      params.set('limit', limit);
      params.set('offset', (page - 1) * limit);

      const response = await api.get(`/destinations?${params.toString()}`);
      setDestinations(response.data.destinations);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Error fetching destinations:', err);
    } finally {
      setLoading(false);
    }
  }, [search, continent, page]);

  useEffect(() => {
    fetchDestinations();
  }, [fetchDestinations]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (continent) params.set('continent', continent);
    setSearchParams(params, { replace: true });
  }, [search, continent, setSearchParams]);

  const totalPages = Math.ceil(total / limit);

  const safetyStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <StarIcon key={i} sx={{ fontSize: 14, color: i < rating ? '#f59e0b' : '#e5e7eb' }} />
    ));
  };

  return (
    <Box sx={documentPageCanvasSx}>
      <SEOHead
        title={`${t('destinations.pageTitle', 'Explore Destinations')} | Déjà Vu Nomad`}
        description={t('destinations.metaDescription', 'Discover travel guides, things to do, weather, and tips for 150+ destinations worldwide. Plan your perfect trip.')}
        canonical="https://dejavunomad.com/destinations"
      />

      <DocumentMarketingHero
        eyebrow={t('nav.destinations')}
        title={t('destinations.heroTitle', 'Explore the World')}
        subtitle={t('destinations.heroSubtitle', 'Discover things to do, local tips, weather, and travel guides for destinations worldwide')}
      >
        <Box sx={{
          display: 'flex',
          maxWidth: 500,
          mx: 'auto',
          bgcolor: DOCUMENT.surface,
          border: `1px solid ${DOCUMENT.ruleHard}`,
          px: 1,
        }}>
          <TextField
            fullWidth
            placeholder={t('destinations.searchPlaceholder', 'Search cities, countries...')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: DOCUMENT.oxblood, ml: 0.5 }} />
                </InputAdornment>
              ),
              sx: { px: 1, py: 1, fontSize: '0.95rem', fontFamily: DOCUMENT.sans }
            }}
          />
        </Box>
      </DocumentMarketingHero>

      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
        {/* Continent Filter Chips */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, justifyContent: 'center' }}>
          <Chip
            label={t('destinations.allDestinations', 'All Destinations')}
            onClick={() => { setContinent(''); setPage(1); }}
            sx={documentFilterChipSx(continent === '')}
          />
          {continents.map(c => (
            <Chip
              key={c.continent}
              label={`${continentEmoji[c.continent] || ''} ${c.continent} (${c.count})`}
              onClick={() => { setContinent(c.continent); setPage(1); }}
              sx={documentFilterChipSx(continent === c.continent)}
            />
          ))}
        </Box>

        {/* Featured Section (only on first page with no search/filter) */}
        {!search && !continent && page === 1 && featured.length > 0 && (
          <Box sx={{ mb: 5 }}>
            <Typography sx={{ ...documentEyebrowSx, mb: 2 }}>
              {t('destinations.featured', 'Featured Destinations')}
            </Typography>
            <Grid container spacing={2}>
              {/* First featured destination - large card */}
              <Grid item xs={12} md={6}>
                <Card
                  onClick={() => navigate(`/destinations/${featured[0].slug}`)}
                  sx={{
                    cursor: 'pointer', borderRadius: 3, overflow: 'hidden',
                    height: '100%', minHeight: { xs: 220, md: 350 },
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 16px 40px rgba(0,0,0,0.15)' },
                    '&:hover .overlay': { background: 'linear-gradient(to bottom, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)' },
                  }}
                >
                  <CardMedia
                    component="img"
                    image={featured[0].image_url || `https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&h=500&fit=crop`}
                    alt={featured[0].name}
                    sx={{ height: '100%', minHeight: { xs: 220, md: 350 }, objectFit: 'cover' }}
                  />
                  <UnsplashAttribution
                    imageUrl={featured[0].image_url}
                    photographerName={featured[0].unsplash_photographer_name}
                    photographerUsername={featured[0].unsplash_photographer_username}
                    variant="overlay"
                  />
                  <Box className="overlay" sx={{
                    position: 'absolute', top: 0, left: 0, right: 0,
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.2) 60%, transparent 100%)',
                    p: { xs: 2, md: 3 }, color: 'white', transition: 'all 0.3s ease',
                  }}>
                    <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight={800}>{featured[0].name}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      <LocationIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      {featured[0].country}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5, display: { xs: 'none', md: 'block' } }}>
                      {featured[0].short_description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      {(featured[0].popular_for ? (typeof featured[0].popular_for === 'string' ? JSON.parse(featured[0].popular_for) : featured[0].popular_for) : []).slice(0, 3).map(tag => (
                        <Chip key={tag} label={tag} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.7rem', height: 24 }} />
                      ))}
                    </Box>
                  </Box>
                </Card>
              </Grid>
              {/* Remaining featured - smaller cards */}
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  {featured.slice(1, 5).map(dest => (
                    <Grid item xs={6} key={dest.id}>
                      <Card
                        onClick={() => navigate(`/destinations/${dest.slug}`)}
                        sx={{
                          cursor: 'pointer', borderRadius: 3, overflow: 'hidden',
                          position: 'relative', height: { xs: 140, md: 168 },
                          transition: 'all 0.3s ease',
                          '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 32px rgba(0,0,0,0.12)' },
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={dest.image_url || `https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=400&h=300&fit=crop`}
                          alt={dest.name}
                          sx={{ height: '100%', objectFit: 'cover' }}
                        />
                        <UnsplashAttribution
                          imageUrl={dest.image_url}
                          photographerName={dest.unsplash_photographer_name}
                          photographerUsername={dest.unsplash_photographer_username}
                          variant="overlay"
                        />
                        <Box sx={{
                          position: 'absolute', top: 0, left: 0, right: 0,
                          background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%)',
                          p: 1.5, color: 'white',
                        }}>
                          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>{dest.name}</Typography>
                          <Typography variant="caption" sx={{ opacity: 0.85 }}>{dest.country}</Typography>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Results Count */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {loading ? t('common.loading', 'Loading...') : t('destinations.showingResults', { count: destinations.length, total })}
          </Typography>
        </Box>

        {/* Loading Skeletons */}
        {loading && (
          <Grid container spacing={2}>
            {Array.from({ length: 12 }).map((_, i) => (
              <Grid item xs={6} sm={4} md={3} key={i}>
                <Skeleton variant="rounded" height={240} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Destination Grid */}
        {!loading && destinations.length > 0 && (
          <Grid container spacing={2}>
            {destinations.map(dest => {
              const tags = dest.popular_for ? (typeof dest.popular_for === 'string' ? JSON.parse(dest.popular_for) : dest.popular_for) : [];
              return (
                <Grid item xs={6} sm={4} md={3} key={dest.id}>
                  <Card
                    onClick={() => navigate(`/destinations/${dest.slug}`)}
                    sx={{
                      cursor: 'pointer', borderRadius: 3, overflow: 'hidden',
                      border: '1px solid', borderColor: 'divider',
                      transition: 'all 0.3s ease',
                      height: '100%',
                      display: 'flex', flexDirection: 'column',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                        '& .dest-image': { transform: 'scale(1.05)' },
                      },
                    }}
                  >
                    {/* Image */}
                    <Box sx={{ position: 'relative', height: { xs: 120, sm: 150, md: 160 }, overflow: 'hidden' }}>
                      {dest.image_url ? (
                        <CardMedia
                          className="dest-image"
                          component="img"
                          image={dest.image_url}
                          alt={dest.name}
                          sx={{ height: '100%', objectFit: 'cover', transition: 'transform 0.4s ease' }}
                        />
                      ) : (
                        <Box sx={{
                          height: '100%',
                          background: DOCUMENT.oxblood,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <ExploreIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                        </Box>
                      )}
                      <UnsplashAttribution
                        imageUrl={dest.image_url}
                        photographerName={dest.unsplash_photographer_name}
                        photographerUsername={dest.unsplash_photographer_username}
                        variant="overlay"
                      />
                      {/* Continent badge */}
                      <Chip
                        label={dest.continent}
                        size="small"
                        sx={{
                          position: 'absolute', top: 8, right: 8,
                          bgcolor: 'rgba(0,0,0,0.5)', color: 'white',
                          fontSize: '0.65rem', height: 22, fontWeight: 600,
                          backdropFilter: 'blur(4px)',
                        }}
                      />
                    </Box>

                    <CardContent sx={{ p: { xs: 1.5, md: 2 }, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight={700} sx={{ lineHeight: 1.2, mb: 0.25 }}>
                        {dest.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mb: 0.5 }}>
                        <LocationIcon sx={{ fontSize: 12 }} /> {dest.country}
                      </Typography>

                      <Typography variant="caption" color="text.secondary" sx={{
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        overflow: 'hidden', lineHeight: 1.4, mb: 1, flexGrow: 1,
                        fontSize: { xs: '0.65rem', sm: '0.72rem' },
                      }}>
                        {dest.short_description}
                      </Typography>

                      {/* Tags */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {tags.slice(0, isMobile ? 2 : 3).map(tag => (
                          <Chip key={tag} label={tag} size="small" sx={{
                            fontSize: '0.6rem', height: 20, fontWeight: 500,
                            bgcolor: 'rgba(74,14,21,0.08)',
                            color: DOCUMENT.oxblood,
                          }} />
                        ))}
                        {tags.length > (isMobile ? 2 : 3) && (
                          <Chip label={`+${tags.length - (isMobile ? 2 : 3)}`} size="small" sx={{
                            fontSize: '0.6rem', height: 20, fontWeight: 500,
                            bgcolor: '#f3f4f6', color: '#6b7280',
                          }} />
                        )}
                      </Box>

                      {/* Quick info row */}
                      <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        {dest.avg_daily_budget && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3, fontSize: '0.65rem' }}>
                            <BudgetIcon sx={{ fontSize: 12 }} /> {dest.avg_daily_budget}
                          </Typography>
                        )}
                        {dest.safety_rating && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {safetyStars(dest.safety_rating)}
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* No Results */}
        {!loading && destinations.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <ExploreIcon sx={{ fontSize: 64, color: '#d1d5db', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">{t('destinations.noResults', 'No destinations found')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('destinations.tryDifferent', 'Try a different search or filter')}</Typography>
            <Button variant="outlined" sx={{ ...partnerOutlineBtnSx, mt: 2 }} onClick={() => { setSearch(''); setContinent(''); }}>
              {t('destinations.clearFilters', 'Clear Filters')}
            </Button>
          </Box>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, v) => { setPage(v); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Destinations;
